import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import { useTranslation } from 'react-i18next';
import { useViewLevel } from '@eis/core';
import { cardSurfaceSx } from '../layout/surface';

export interface DetailField {
  readonly label: string;
  readonly value: ReactNode;
  /** Optional provenance marker, e.g. "Von Gruppen-Police geerbt" (SSOT I-12). */
  readonly source?: ReactNode;
  /** Spans the full width — for long text or nested content. */
  readonly wide?: boolean;
  /**
   * Marks the field the reader was sent to look at — a search hit's field (SDD-017 §6 `hl`). A
   * tinted, outlined cell plus a "Treffer" caption, so the mark is not colour alone.
   */
  readonly highlighted?: boolean;
}

export interface DetailsSectionProps {
  readonly title: string;
  readonly description?: string;
  readonly fields: readonly DetailField[];
  /**
   * Expert fields — technical ids, pinning details, digests. Hidden at ViewLevel `standard`
   * and revealed at `advanced`, so the default screen stays readable for the operations persona
   * without the information being unavailable to the people who need it.
   */
  readonly advancedFields?: readonly DetailField[];
  readonly actions?: ReactNode;
  readonly children?: ReactNode;
}

function FieldList({ fields }: { readonly fields: readonly DetailField[] }): ReactNode {
  const { t } = useTranslation('patterns');
  return (
    <Box
      component="dl"
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, minmax(0, 1fr))',
          lg: 'repeat(3, minmax(0, 1fr))',
        },
        columnGap: 3,
        rowGap: 1.75,
        m: 0,
      }}
    >
      {fields.map((field) => (
        <Box
          key={field.label}
          data-highlighted={field.highlighted === true ? 'true' : undefined}
          sx={{
            gridColumn: field.wide === true ? '1 / -1' : 'auto',
            minWidth: 0,
            ...(field.highlighted === true
              ? {
                  bgcolor: 'action.selected',
                  outline: 2,
                  outlineColor: 'primary.main',
                  borderRadius: 1,
                  px: 1,
                  py: 0.5,
                }
              : {}),
          }}
        >
          <Typography variant="caption" color="text.secondary" component="dt">
            {field.label}
            {field.highlighted === true && (
              <Chip
                size="small"
                color="primary"
                label={t('details.highlighted')}
                sx={{ ml: 1, height: 18 }}
              />
            )}
          </Typography>
          <Box component="dd" sx={{ m: 0, mt: 0.25 }}>
            <Typography
              variant="body2"
              component="span"
              // Berry's value is 14/600 under the 12px label; suite's stays body2 (theme tokens).
              sx={(theme) => ({ display: 'block', ...theme.eis.surface.fieldValue })}
            >
              {field.value ?? t('details.notSet')}
            </Typography>
            {field.source !== undefined && <Box sx={{ mt: 0.5 }}>{field.source}</Box>}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

/**
 * The read-only counterpart to FormSection, and the reason this repo bans card-per-field layouts:
 * a dense definition list shows twenty facts in the space one card wastes on three.
 */
export function DetailsSection({
  title,
  description,
  fields,
  advancedFields,
  actions,
  children,
}: DetailsSectionProps): ReactNode {
  const { t } = useTranslation('patterns');
  const { isAdvanced } = useViewLevel();
  const showAdvanced = isAdvanced && advancedFields !== undefined && advancedFields.length > 0;

  return (
    <Paper
      variant="outlined"
      component="section"
      sx={(theme) => ({
        p: 2.5,
        ...cardSurfaceSx(theme),
        // Prompt 22's primary left rule — suite keeps it; the EIS design (berry) has none.
        ...(theme.eis.surface.sectionAccentRule
          ? { borderLeftWidth: 4, borderLeftStyle: 'solid', borderLeftColor: 'primary.main' }
          : {}),
      })}
    >
      <Stack
        direction="row"
        spacing={2}
        useFlexGap
        sx={{ alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', mb: 2 }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h3" component="h2">
            {title}
          </Typography>
          {description !== undefined && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              {description}
            </Typography>
          )}
        </Box>
        {actions !== undefined && (
          <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
            {actions}
          </Stack>
        )}
      </Stack>

      <FieldList fields={fields} />

      {showAdvanced && (
        <>
          <Divider sx={{ my: 2.5 }}>
            <Chip label={t('details.advanced')} size="small" variant="outlined" />
          </Divider>
          <FieldList fields={advancedFields} />
        </>
      )}

      {children !== undefined && <Box sx={{ mt: 2 }}>{children}</Box>}
    </Paper>
  );
}
